/**
 * "Back to first image" control for the product media gallery.
 * Companion to media-gallery.js — jumps the gallery back to its first
 * visible media item via the same code path as a thumbnail click.
 */
if (!customElements.get('media-back-to-first')) {
  class MediaBackToFirst extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.gallery = this.closest('media-gallery');
      if (!this.button || !this.gallery) return;

      this.clickHandler = this.handleClick.bind(this);
      this.button.addEventListener('click', this.clickHandler);

      // media-gallery rewrites its status live region on every slide change —
      // observe it to keep this button's active/inactive state in sync.
      this.status = this.gallery.querySelector('.media-gallery__status');
      if (this.status) {
        this.observer = new MutationObserver(this.syncState.bind(this));
        this.observer.observe(this.status, { childList: true });
      }

      this.syncState();
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.clickHandler);
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Reflects the gallery position on the button. aria-disabled (rather than
     * disabled) keeps the button focusable when it deactivates under keyboard
     * focus. gallery.currentIndex is undefined until the gallery initialises —
     * treat that as "on the first image".
     */
    syncState() {
      const onFirstItem = !this.gallery.currentIndex;
      this.button.setAttribute('aria-disabled', onFirstItem ? 'true' : 'false');
    }

    /**
     * Handles 'click' events on the button (Enter/Space included).
     */
    handleClick() {
      if (this.button.getAttribute('aria-disabled') === 'true') return;

      const firstItem = this.gallery.visibleItems && this.gallery.visibleItems[0];
      if (!firstItem || typeof this.gallery.customSetActiveMedia !== 'function') return;

      // Same path as a thumbnail click: instant scroll + counter/arrow/thumb
      // updates + "Image 1 is now available" live region announcement.
      this.gallery.customSetActiveMedia(firstItem);
    }
  }

  customElements.define('media-back-to-first', MediaBackToFirst);
}
